import { useCallback, useEffect, useState } from 'react';
import { Ship, RefreshCcw } from 'lucide-react';
import { apiFetchJson } from '../lib/api';

// 出海診斷預約後台（live-funnel 組件；Admin Form First）
// 列表＋狀態流轉（new→contacted→converted/closed）＋內部備註。
// WhatsApp 一鍵跳轉（wa.me）方便跟進。

interface Lead {
  id: number;
  contactName: string;
  whatsapp: string;
  brandName: string | null;
  productType: string | null;
  targetMarkets: string | null;
  notesFromLead: string | null;
  status: string;
  adminNotes: string | null;
  source: string | null;
  createdAt: number;
}

const STATUS_LABELS: Record<string, string> = {
  new: '🆕 新預約',
  contacted: '📞 已聯絡',
  converted: '✅ 已成交',
  closed: '📦 已結案',
};

const PRODUCT_LABELS: Record<string, string> = {
  food: '食品／飲品', health: '保健品', beauty: '美妝', daily: '日用品', other: '其他',
};

export default function AdminB2BLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [msg, setMsg] = useState('');
  const [notesDraft, setNotesDraft] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : '';
      const res = await apiFetchJson<Lead[] | { data?: Lead[] }>(`/public/admin/live-funnel/leads${q}`);
      const list = Array.isArray(res) ? res : res?.data || [];
      setLeads(list);
      setNotesDraft(Object.fromEntries(list.filter((l) => l.adminNotes).map((l) => [l.id, l.adminNotes as string])));
    } catch {
      setMsg('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const flash = (t: string) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };

  const patch = async (id: number, body: Record<string, unknown>) => {
    const res = await apiFetchJson<{ success: boolean; error?: string }>(`/public/admin/live-funnel/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    if (!res.success) { flash(res.error || '更新失敗'); return; }
    flash('已更新');
    load();
  };

  const fmtTime = (ts: number) => new Date(ts * 1000).toLocaleString('zh-HK', { hour12: false });

  const inputCls = 'border border-stone-300 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-x1493-primary/30';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ship size={24} className="text-x1493-primary" />
            出海診斷預約
          </h1>
          <p className="text-sm text-stone-500 mt-1">直播漏斗 bot 的 B2B 名單（/b2b 表單來源）——狀態流轉：新預約 → 已聯絡 → 已成交／已結案。</p>
        </div>
        <div className="flex gap-2 items-center">
          <select className={inputCls + ' w-40'} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">全部狀態</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={load} className="p-2.5 border rounded-xl hover:bg-stone-50" title="重新載入"><RefreshCcw size={16} /></button>
        </div>
      </div>

      {msg && <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-2 text-sm">{msg}</div>}

      <div className="bg-white border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3">聯絡人</th>
              <th className="px-4 py-3">品牌／貨品</th>
              <th className="px-4 py-3">目標市場</th>
              <th className="px-4 py-3">補充</th>
              <th className="px-4 py-3">時間</th>
              <th className="px-4 py-3">狀態</th>
              <th className="px-4 py-3">跟進備註</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-400">載入中...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-400">暫無預約——直播開波後名單會出現在這裡</td></tr>
            ) : leads.map((l) => (
              <tr key={l.id} className="border-t align-top">
                <td className="px-4 py-3">
                  <p className="font-bold">{l.contactName}</p>
                  <a href={`https://wa.me/${l.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-x1493-primary text-xs hover:underline">
                    {l.whatsapp} ↗
                  </a>
                </td>
                <td className="px-4 py-3">
                  {l.brandName && <p className="font-bold">{l.brandName}</p>}
                  <p className="text-stone-500 text-xs">{PRODUCT_LABELS[l.productType || 'other'] || l.productType}</p>
                </td>
                <td className="px-4 py-3 text-xs">{(l.targetMarkets || '').split(',').filter(Boolean).join('、') || '—'}</td>
                <td className="px-4 py-3 text-xs text-stone-500 max-w-40">{l.notesFromLead || '—'}</td>
                <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">{fmtTime(l.createdAt)}</td>
                <td className="px-4 py-3">
                  <select
                    className="border border-stone-300 rounded-lg px-2 py-1.5 text-xs font-bold"
                    value={l.status}
                    onChange={(e) => patch(l.id, { status: e.target.value })}
                  >
                    {Object.entries(STATUS_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 min-w-44">
                    <input
                      className="border border-stone-200 rounded-lg px-2 py-1.5 text-xs w-full"
                      placeholder="內部備註…"
                      value={notesDraft[l.id] || ''}
                      onChange={(e) => setNotesDraft({ ...notesDraft, [l.id]: e.target.value })}
                    />
                    <button
                      onClick={() => patch(l.id, { adminNotes: notesDraft[l.id] || '' })}
                      className="bg-x1493-primary text-white text-xs font-bold px-2.5 rounded-lg hover:brightness-110"
                    >
                      存
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
